import React, { useMemo, useImperativeHandle, forwardRef, FC, useEffect } from 'react';
import {
  ReportObjectContainer,
  ReportContainerProviders,
  ReportStoreSuspense,
} from '@sas-dvr/internal-va-react-core/components/ReportContainer';
import { ReportContentWrapper } from '@sas-dvr/internal-va-react-core/components/ReportContentWrapper';
import { useReportContext, useReportStore } from '../internal/hooks';
import { AbstractReportProps } from './SASReport';
import { createUniqueId } from '@sas-dvr/internal-va-react-core/components/utils/createUniqueId';
import { ReportHandle } from '../handles';
import { useRootClassName } from '../config';
import { BirdState } from '@sas-dvr/internal-va-react-core/store/bird/birdState';
import { Provider } from 'react-redux';
import { CoreContextProviders } from '@sas-dvr/internal-va-react-core/components/CoreContextProviders';

type BaseObjectProps = {
  reportContextKey?: string;
  objectName: string;
  onReportObjectLoad?: () => void;
  onContentComplete?: () => void;
};
export type SASReportObjectProps = BaseObjectProps & AbstractReportProps;

// Typed as FC so the mobile wrappers don't have to be forward ref components. forwardRef also mangles unions of prop types.
export const SASReportObject: FC<SASReportObjectProps> = forwardRef<
  ReportHandle,
  SASReportObjectProps
>(function SASReportObject(props, ref) {
  const elementKey = useMemo(() => {
    return props.elementKey === undefined ? createUniqueId() : props.elementKey;
  }, [props.elementKey]);

  const contextProps =
    'url' in props
      ? {
          url: props.url,
          reportUri: props.reportUri,
          authenticationType: props.authenticationType,
        }
      : {
          packageUri: props.packageUri,
        };

  const context = useReportContext({
    ...contextProps,
    objectName: props.objectName,
    contextKey: props.reportContextKey,
  });
  useImperativeHandle(ref, () => context.getReportHandle(), [context]);

  const store = useReportStore(context, elementKey, props.objectName);
  useEffect(() => {
    store.dispatch(BirdState.actions.setRestrictViewportGestures(props.restrictViewportGestures));
  }, [store, props.restrictViewportGestures]);

  const rootClassName = useRootClassName();
  return (
    <Provider store={store}>
      <ReportContentWrapper className={rootClassName}>
        <CoreContextProviders popoverRootId={props.popoverRootId}>
          <ReportContainerProviders
            url={contextProps.url}
            menuItemProvider={props.menuItemProvider}
            elementType="object"
            authenticationType={contextProps.authenticationType}
          >
            <ReportStoreSuspense
              url={contextProps.url}
              authenticationType={contextProps.authenticationType}
            >
              <ReportObjectContainer {...props} />
            </ReportStoreSuspense>
          </ReportContainerProviders>
        </CoreContextProviders>
      </ReportContentWrapper>
    </Provider>
  );
});
